import { action, computed, observable } from 'mobx';
import { UsersStore } from '../../common/stores';
import { OrderBy } from '../../common/types';
import BaseHistoryRecordModel from '../models/BaseHistoryRecordModel';
import HistoryService from '../services/HistoryService';
import { HistoryRecordFactory } from '../misc';
import { GlobalAdministrationService } from '../../administration/service/GlobalAdministrationService';
import { Group } from '../../administration/service/GlobalAdministrationService';
import { ProjectAssignmentHistoryRecordModel } from '../models/project_assignment';
import { ObjectRoleAssignmentHistoryRecordModel } from '../models/object_role_assignment';

type FieldOrderBy = OrderBy;

export default class AdministrationHistoryStore {
    static historyAreaOptions = [
        {
            value: 'users',
            label: 'Users',
            entities: ['User']
        },
        {
            value: 'projects',
            label: 'Deleted Projects',
            entities: ['Project']
        },
        {
            value: 'projectAssignment',
            label: 'Project Assignments',
            entities: ['ProjectAssignment']
        },
        {
            value: 'objectRoleAssignment',
            label: 'Role Assignments',
            entities: ['ObjectRoleAssignment', 'CustomRoleAssignment']
        },
        {
            value: 'customRole',
            label: 'Roles',
            entities: ['CustomRole']
        },
        {
            value: 'apiKeys',
            label: 'API Keys',
            entities: ['ApiKeyCredentialRecord']
        }
    ];

    static HISTORY_PAGE_SIZE = 20;

    @observable
    historyRecords: BaseHistoryRecordModel[] = [];

    @observable
    total: number = 0;

    @observable
    orderBy?: FieldOrderBy;

    @observable
    selectedAreaOption?: string;

    @observable
    page: number = 0;

    @observable
    isLoading: boolean = false;

    @observable
    isLoadingGroups: boolean = false;

    @observable
    searchTerm: string = '';

    @observable
    userId?: string;

    @observable
    dateFrom?: string;

    @observable
    dateTo?: string;

    @observable
    diffSplitViewEnabled: boolean = false;

    @observable
    groups: Group[] = [];

    @computed
    get searchEntities() {
        return (
            AdministrationHistoryStore.historyAreaOptions.find(o => o.value === this.selectedAreaOption)?.entities ?? []
        );
    }

    @computed
    get userFilterOptions() {
        return this.usersStore.resolveUsers([...this.historyRecords.map(record => record.userId), this.userId]);
    }

    @computed
    get isLoadingUsers() {
        return this.usersStore.isLoading;
    }

    constructor(
        readonly usersStore: UsersStore,
        readonly historyService: HistoryService,
        readonly globalAdministrationService: GlobalAdministrationService
    ) {}

    @action.bound
    setHistoryRecords(historyRecords: BaseHistoryRecordModel[]) {
        this.historyRecords = historyRecords;
    }

    @action.bound
    setTotal(total: number) {
        this.total = total;
    }

    @action.bound
    setOrderBy(orderBy?: FieldOrderBy) {
        this.orderBy = orderBy;
    }

    @action.bound
    setSelectedAreaOption(selectedAreaOption?: string) {
        this.selectedAreaOption = selectedAreaOption;
    }

    @action.bound
    setPage(page: number) {
        this.page = page;
    }

    @action.bound
    setSearchTerm(searchTerm: string) {
        this.searchTerm = searchTerm;
    }

    @action.bound
    setUserId(userId?: string) {
        this.userId = userId;
    }

    @action.bound
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @action.bound
    setIsLoadingGroups(isLoadingGroups: boolean) {
        this.isLoadingGroups = isLoadingGroups;
    }

    @action.bound
    setDateFrom(dateFrom?: string) {
        this.dateFrom = dateFrom;
    }

    @action.bound
    setDateTo(dateTo?: string) {
        this.dateTo = dateTo;
    }

    @action.bound
    performSearch() {
        this.setPage(0);
        this.searchHistory();
    }

    @action.bound
    toggleDiffSplitView() {
        this.diffSplitViewEnabled = !this.diffSplitViewEnabled;
    }

    @action.bound
    async searchHistory() {
        try {
            this.setIsLoading(true);

            const trimmedSearchTerm = this.searchTerm.trim();

            const response = await this.historyService.searchAdministrationHistory({
                searchEntities: this.searchEntities,
                orderBy: this.orderBy,
                page: this.page,
                pageSize: AdministrationHistoryStore.HISTORY_PAGE_SIZE,
                searchTerm: trimmedSearchTerm,
                entityIds:
                    trimmedSearchTerm.length > 0
                        ? this.findMatchingUsersAndGroupsBySearchTerm(trimmedSearchTerm)
                        : undefined,
                userId: this.userId,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo
            });

            const historyRecords = response.historyRecords.map(HistoryRecordFactory.createHistoryRecord);

            this.setHistoryRecords(historyRecords);
            this.setTotal(response.total);
        } catch (error) {
            console.error(error);
        } finally {
            this.setIsLoading(false);
        }
    }

    @action.bound
    async getGroups() {
        try {
            this.setIsLoadingGroups(true);
            this.groups = await this.globalAdministrationService.getGroups();
        } catch (error) {
            console.error(error);
        } finally {
            this.setIsLoadingGroups(false);
        }
    }

    @action.bound
    clearFilters() {
        this.setSearchTerm('');
        this.setUserId(undefined);
        this.setSelectedAreaOption(undefined);
        this.setDateFrom(undefined);
        this.setDateTo(undefined);
    }

    dispose() {
        this.clearFilters();
        this.setHistoryRecords([]);
        this.setTotal(0);
        this.setPage(0);
    }

    getRecordDisplayName(record: BaseHistoryRecordModel): string | null {
        if (
            record instanceof ObjectRoleAssignmentHistoryRecordModel ||
            (record instanceof ProjectAssignmentHistoryRecordModel && record.isUserProjectAssignment)
        ) {
            const id = record.assignmentObjectId;
            return id ? this.usersStore.getUserById(id)?.userName || record.displayName || id : record.displayName;
        }
        if (record instanceof ProjectAssignmentHistoryRecordModel) {
            return (
                this.groups.find(group => group.usersGroup.id === record.assignmentObjectId)?.usersGroup.name ||
                record.displayName
            );
        }
        return record.displayName;
    }

    private findMatchingUsersAndGroupsBySearchTerm(searchTerm: string): string[] {
        const text = searchTerm.toLowerCase();

        const matchedUserIds = this.usersStore.users
            .filter(u => u.userName.toLowerCase().includes(text))
            .map(u => u.id);

        const matchedGroupIds = this.groups
            .filter(g => g.usersGroup?.name && g.usersGroup.name.toLowerCase().includes(text))
            .map(g => g.usersGroup.id);

        return [...matchedUserIds, ...matchedGroupIds];
    }
}
